/**
 * 左のメニュー「送付状」と送付の記録の PDF の画面（#223 の3回目の前の直し、2026-10-21 射場さんの決め＝docs/89 ★12・★18、§17.3・§17.6）
 *   CoverLettersPage /cover-letters：送付の記録の一覧（会社の全件・新しい順・100件ずつ「さらに表示」）。右上の主「送付状作成」（PC だけ＝PC_ONLY_FEATURES の
 *     「送付状の作成」。スマホ版は一覧を見るだけ）→ 単体送付状・束ねの画面 /cover-letters/new
 *   DeliveryPage /deliveries/:id：送付の記録の PDF。**保存した PDF をそのまま使う（作り直さない）**＝GET /deliveries/:id/pdf を画面で描いてページ画像に
 *     （受け取った書類の引き出しと同じ部品 components/PageViewer.tsx：−・倍率・＋・幅に合わせる・P.n／m）。
 *     上の帯：‹ 戻る（来た画面へ。無ければ一覧）・題・送付先・日付・型・枚数・送付者／右に「ダウンロード」（保存した PDF のバイトのまま）・「印刷」（PC だけ。
 *     保存した PDF のページを印刷の仕組み #printContainer で刷る＝紙の画面と同じ。横のページは縦の紙に回す）
 * 送信待ち（#268 1.5c-2、docs/98 §7）：sales の会社はタブ「送信待ち n」（?f=queue）＝まとめてメールで上限に達した残り（components/MailQueue.tsx）
 * メール送付（#225、docs/91 §3.4）：
 *   一覧にタブ「すべて｜要確認」（?f=attention＝届いていません・送信失敗・未取得 7日。確認済みは除く）
 *   記録の画面にメールの節：状態・宛先・Cc・Bcc・件名・渡し方・公開期限・送付書類・取得の記録（自社の取得は「自社」）・「閲覧ページ無効化」（理由は任意）・「確認済み」
 */
import { COVER_LETTER_MSG as M, MAIL_ACCESS_LABEL, MAIL_LIMITS, MAIL_MODE_LABEL, MAIL_MSG, MAIL_QUEUE_MSG, deliveryManualText, deliveryModeText, sizeText, type DeliveryDetail } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams, useSearchParams } from "react-router";
import { api, fetchFile, messageOf } from "../api.js";
import { BackButton, useBackNav, usePageName } from "../components/BackButton.jsx";
import { DeliveriesAll, MailStateChip } from "../components/Deliveries.jsx";
import { MailQueuePanel, openCountOf, useMailQueue } from "../components/MailQueue.jsx";
import { ListHead } from "../components/ListHead.jsx";
import { Icon } from "../components/Shell.jsx";
import { ListTabs, listTabPanel } from "../components/ListTabs.jsx";
import { PageViewer, type ViewerPage, type Zoom } from "../components/PageViewer.jsx";
import { ErrorText, Field, Help, useToast } from "../components/ui.jsx";
import { useModule } from "../core/slots.jsx";
import { fmtDateTimeShort } from "../lib/dates.js";
import { usePcOnly } from "../lib/pcOnly.js";
import { downloadBlob, portraitForPrint, renderPdf, type PageImg } from "../lib/pdfPages.js";
import { useNarrow } from "../lib/useNarrow.js";
import { currentView } from "../lib/view.js";

export function CoverLettersPage() {
  usePageName(M.listTitle);
  const pcOnly = usePcOnly();
  const [sp, setSp] = useSearchParams();
  // 送信待ち（#268 1.5c-2）：まとめてメールは門 sales＝sales の会社だけタブを出す
  const sales = useModule("sales");
  const f: "all" | "attention" | "queue" = sp.get("f") === "attention" ? "attention" : sp.get("f") === "queue" && sales ? "queue" : "all";
  const queue = useMailQueue(sales);
  return (
    <div className="dlvlist" data-testid="cover-letters-page">
      <ListHead
        titleTestId="cover-letters-title"
        title={
          <>
            {M.listTitle}
            <Help testId="help-cover-letters" label={`${M.listTitle}の説明`} text={M.listHelp} />
          </>
        }
      >
      <div className="lcard">
      <ListTabs
        id="dlvl"
        label="送付の記録を状態で絞る"
        tabs={[
          { key: "all" as const, label: MAIL_MSG.tabAll },
          { key: "attention" as const, label: MAIL_MSG.tabAttention },
          ...(sales ? [{ key: "queue" as const, label: MAIL_QUEUE_MSG.title, count: openCountOf(queue.data?.items) || undefined }] : []),
        ]}
        value={f}
        onChange={(k) =>
          setSp((p) => {
            const n = new URLSearchParams(p);
            if (k === "all") n.delete("f");
            else n.set("f", k);
            return n;
          })
        }
        create={
          pcOnly.hidden("/cover-letters/new") ? null : (
            <Link className="btn primary" to="/cover-letters/new" data-testid="cover-letters-create">
              <Icon k="plus" />
              {M.create}
            </Link>
          )
        }
      />
      <div className="ltab-panel" {...listTabPanel("dlvl", f)}>
        {f === "queue" ? <MailQueuePanel /> : <DeliveriesAll key={f} attention={f === "attention"} />}
      </div>
      </div>
      </ListHead>
    </div>
  );
}

/** ページ画像の大きさ：長い辺を 1600px として渡す（PageViewer の 100%＝受け取った書類の表示用の画像と同じ縮尺＝A4 縦の幅がほぼ紙の原寸） */
const viewerPagesOf = (pages: PageImg[]): ViewerPage[] =>
  pages.map((p, i) => {
    const k = 1600 / Math.max(p.w, p.h);
    return { n: i + 1, url: p.url, width: Math.round(p.w * k), height: Math.round(p.h * k) };
  });

export function DeliveryPage() {
  const { id = "" } = useParams();
  const back = useBackNav({ to: "/cover-letters", label: M.listTitle });
  usePageName(M.pdfPageTitle);
  const narrow = useNarrow() || currentView() === "phone";
  const d = useQuery({ queryKey: ["delivery", id], queryFn: () => api<DeliveryDetail>("GET", `/deliveries/${id}`), retry: false });
  // 保存した PDF（作り直さない）。描いた画像は画面とページ送り・印刷に使い、ダウンロードはバイトのまま
  const pdf = useQuery({
    queryKey: ["delivery-pdf", id],
    queryFn: async () => {
      const blob = await fetchFile(`/api/v1/deliveries/${id}/pdf`);
      const pages: PageImg[] = [];
      await renderPdf(await blob.arrayBuffer(), (p, i) => {
        pages[i - 1] = p;
      });
      return { blob, pages };
    },
    enabled: !!d.data?.pdfUrl,
    retry: false,
    staleTime: Infinity,
  });
  const [zoom, setZoom] = useState<Zoom>("fit");
  const [printPages, setPrintPages] = useState<PageImg[] | null>(null);
  // 印刷用の見た目（styles.css の body.paper-mode）。この画面にいる間だけ
  useEffect(() => {
    document.body.classList.add("paper-mode");
    return () => document.body.classList.remove("paper-mode");
  }, []);
  useEffect(() => {
    if (!printPages) return;
    const f = requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    return () => cancelAnimationFrame(f);
  }, [printPages]);
  const v = d.data;
  const doPrint = async () => {
    if (!pdf.data) return;
    // 送付状（A4 縦）に A4 横の同封書類が混じるときは、横のページを縦の紙に回す（紙の画面の印刷と同じ）
    setPrintPages(await portraitForPrint(pdf.data.pages));
  };
  return (
    <div className="dlvpage" data-testid="delivery-page">
      <style>{"@page { size: A4; margin: 0; }"}</style>
      <BackButton back={back} />
      <div className="hrow paperbar dlvbar">
        <h1>{M.pdfPageTitle}</h1>
        {v ? (
          <span className="dlvmeta" data-testid="delivery-meta">
            <b>
              {[v.partnerName, v.branchName].filter(Boolean).join(" ") || "—"}
              {v.unregistered ? <span className="chip dlv-un">{M.unregisteredMark}</span> : null}
            </b>
            <span className="num">{fmtDateTimeShort(v.at)}</span>
            <span className="chip">{deliveryModeText(v)}</span>
            {deliveryManualText(v) ? <span className="chip">{deliveryManualText(v)}</span> : null}
            {v.pageCount ? <span className="num">{M.pagesUnit.replace("{n}", String(v.pageCount))}</span> : null}
            {v.by ? <span>{`${M.colBy}　${v.by}`}</span> : null}
          </span>
        ) : null}
        <span className="sp" />
        <button type="button" className="btn" disabled={!pdf.data} onClick={() => pdf.data && v && downloadBlob(pdf.data.blob, v.fileName)} data-testid="delivery-download">
          {M.download}
        </button>
        {narrow ? null : (
          <button type="button" className="btn primary" disabled={!pdf.data} onClick={() => void doPrint()} data-testid="delivery-print">
            {M.print}
          </button>
        )}
      </div>
      {d.isError ? <ErrorText>{messageOf(d.error)}</ErrorText> : null}
      {v?.mail && v.mailDetail ? <MailSection v={v} /> : null}
      {v && !v.pdfUrl && !v.mail ? <p className="hint">{M.deliveryNotFound}</p> : null}
      {v?.pdfUrl ? (
        <div className="dlvview">
          <PageViewer
            docKey={id}
            title={M.pdfPageTitle}
            pages={pdf.data ? viewerPagesOf(pdf.data.pages) : undefined}
            error={pdf.error ?? undefined}
            pageCount={v.pageCount}
            zoom={zoom}
            onZoom={setZoom}
            compact={narrow}
            label={M.pdfPageTitle}
            ids={{ root: "delivery-viewer", pages: "delivery-pages", img: "delivery-page-img", page: "delivery-page-no", fit: "delivery-fit", zoom: "delivery-zoom", zoomIn: "delivery-zoom-in", zoomOut: "delivery-zoom-out", toolbar: "delivery-toolbar", preparing: "delivery-preparing" }}
          />
        </div>
      ) : null}
      {back.dialog}
      {createPortal(
        <div id="printContainer" aria-hidden="true">
          {(printPages ?? []).map((p, i) => (
            <div className="printedPage" key={i}>
              <img src={p.url} alt="" width={p.w} height={p.h} />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

/** 記録の画面のメールの節（#225、docs/91 §3.4） */
function MailSection({ v }: { v: DeliveryDetail }) {
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const m = v.mail!;
  const x = v.mailDetail!;
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["delivery", v.id] });
    void qc.invalidateQueries({ queryKey: ["deliveries"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
  };
  const revoke = async () => {
    setBusy(true);
    try {
      await api("POST", `/deliveries/${v.id}/revoke`, { reason: reason.trim() || undefined });
      setRevoking(false);
      toast(MAIL_MSG.revoked);
      refresh();
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };
  const dismiss = async () => {
    try {
      await api("POST", `/deliveries/${v.id}/dismiss`, {});
      toast(MAIL_MSG.dismissed);
      refresh();
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const canRevoke = m.mode === "page" && !x.revokedAt && m.state !== "failed" && m.state !== "expired";
  const ymd = (iso: string | null) => (iso ? fmtDateTimeShort(iso) : "—");
  // 取得の記録の「何番目のファイル」は書類名で出す（番号だけでは何を見たか分からない）
  const fileName = (no: number) => {
    const f = x.files.find((y) => y.no === no);
    return f ? (f.docNo ? `${f.label}（No. ${f.docNo}）` : f.label) : String(no);
  };
  return (
    <section className="paper dlvmail" aria-label={MAIL_MSG.sectionMail} data-testid="delivery-mail">
      <div className="dlvmail-head">
        <h2>{MAIL_MSG.sectionMail}</h2>
        <MailStateChip d={v} />
        <span className="sp" />
        {m.attention ? (
          <button type="button" className="btn text" onClick={() => void dismiss()} title={MAIL_MSG.dismissHelp} data-testid="delivery-dismiss">
            {MAIL_MSG.dismiss}
          </button>
        ) : null}
        {canRevoke ? (
          <button type="button" className="btn" onClick={() => setRevoking(true)} data-testid="delivery-revoke">
            {MAIL_MSG.revoke}
          </button>
        ) : null}
      </div>
      <dl className="dlvmail-dl">
        <dt>{MAIL_MSG.to}</dt>
        <dd className="num" data-testid="delivery-mail-to">{x.to.join(", ")}</dd>
        {x.cc.length ? (
          <>
            <dt>{MAIL_MSG.cc}</dt>
            <dd className="num">{x.cc.join(", ")}</dd>
          </>
        ) : null}
        {x.bcc.length ? (
          <>
            <dt>{MAIL_MSG.bcc}</dt>
            <dd className="num">{x.bcc.join(", ")}</dd>
          </>
        ) : null}
        <dt>{MAIL_MSG.subject}</dt>
        <dd>{x.subject}</dd>
        <dt>{MAIL_MSG.mode}</dt>
        <dd>
          {m.mode === "ack" ? MAIL_MSG.modeAck : MAIL_MODE_LABEL[m.mode]}
          {m.mode === "page" && m.expiresAt ? `（${MAIL_MSG.expires} ${ymd(m.expiresAt)}）` : ""}
          {x.revokedAt ? `　無効 ${ymd(x.revokedAt)}${x.revokeReason ? `（${x.revokeReason}）` : ""}` : ""}
        </dd>
        {x.bouncedAt ? (
          <>
            <dt>{MAIL_MSG.bouncedAt}</dt>
            <dd className="shu">{`${ymd(x.bouncedAt)}${x.bounceCode ? `（${x.bounceCode}）` : ""}`}</dd>
          </>
        ) : null}
        {x.error ? (
          <>
            <dt>{MAIL_MSG.error}</dt>
            <dd className="shu">{x.error}</dd>
          </>
        ) : null}
        <dt>{MAIL_MSG.sentFiles}</dt>
        <dd>
          <ol className="dlvmail-files">
            {x.files.map((f) => (
              <li key={f.no}>
                <span>{f.docNo ? `${f.label}（No. ${f.docNo}）` : f.label}</span>
                <span className="hint num">{sizeText(f.bytes)}</span>
              </li>
            ))}
          </ol>
        </dd>
      </dl>
      <details className="dlvmail-body">
        <summary>{MAIL_MSG.body}</summary>
        <pre>{x.body}</pre>
      </details>
      {m.mode === "page" ? (
        <div className="dlvmail-acc" data-testid="delivery-accesses">
          <h3>
            {MAIL_MSG.accesses}
            <Help label={MAIL_MSG.accesses} text={MAIL_MSG.accessSelfHelp} testId="help-delivery-accesses" />
          </h3>
          {x.accesses.length ? (
            <ul>
              {x.accesses.map((a, i) => (
                <li key={i} data-testid="delivery-access">
                  <span className="num">{fmtDateTimeShort(a.at)}</span>
                  <span>{MAIL_ACCESS_LABEL[a.action]}{a.fileNo ? `　${fileName(a.fileNo)}` : ""}</span>
                  {a.counted ? null : <span className="chip">{MAIL_MSG.accessSelf}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="hint">{MAIL_MSG.accessesEmpty}</p>
          )}
        </div>
      ) : null}
      {revoking ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && setRevoking(false)}>
          <div className="dlg" role="dialog" aria-modal="true" aria-labelledby="dlvrev-h" onKeyDown={(e) => e.key === "Escape" && !busy && setRevoking(false)} data-testid="delivery-revoke-dialog">
            <div className="dh" id="dlvrev-h">
              {MAIL_MSG.revokeTitle}
            </div>
            <div className="db">
              <p>{MAIL_MSG.revokeAsk}</p>
              <Field id="dlvrev-r" label={MAIL_MSG.revokeReason}>
                <input id="dlvrev-r" className="inp" placeholder=" " value={reason} maxLength={MAIL_LIMITS.revokeReason} onChange={(e) => setReason(e.target.value)} data-testid="delivery-revoke-reason" />
              </Field>
            </div>
            <div className="df">
              <button type="button" className="btn" disabled={busy} onClick={() => setRevoking(false)}>
                取消
              </button>
              <button type="button" className="btn primary" disabled={busy} onClick={() => void revoke()} data-testid="delivery-revoke-confirm">
                {MAIL_MSG.revoke}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {node}
    </section>
  );
}
